import { useState, useCallback } from 'react';
import { MatchConfig, PlayerSide, ScoreState } from './types';

// Internal representation: 0=0, 1=15, 2=30, 3=40, 4=Adv

const INITIAL_STATE: Omit<ScoreState, 'history'> = {
    sets: [{ t1: 0, t2: 0 }],
    currentGame: { t1: 0, t2: 0 },
    currentSetIndex: 0,
    isTieBreak: false,
    isSuperTieBreak: false,
    isFinished: false,
    winner: null,
};

export const usePadelScore = (initialConfig: MatchConfig) => {
    const [config, setConfig] = useState<MatchConfig>(initialConfig);

    // We store the full state including history in one object or separate?
    // Let's keep history inside the state object for easier undo
    const [state, setState] = useState<ScoreState>({
        ...INITIAL_STATE,
        history: []
    });

    // Helper to push history
    const pushHistory = useCallback((currentState: ScoreState) => {
        // We only store the "playable" state, not the history itself to avoid recursion/bloat
        const { history, ...stateToSave } = currentState;
        return [stateToSave, ...history].slice(0, 50); // Keep last 50 moves
    }, []);

    const undo = useCallback(() => {
        setState(prev => {
            if (prev.history.length === 0) return prev;
            const [lastState, ...remainingHistory] = prev.history;
            return {
                ...lastState,
                history: remainingHistory
            } as ScoreState;
        });
    }, []);

    const reset = useCallback(() => {
        setState({
            ...INITIAL_STATE,
            history: []
        });
    }, []);

    const addPoint = useCallback((team: PlayerSide) => {
        setState(prev => {
            if (prev.isFinished) return prev;

            // Deep copy state to avoid mutation and fix Undo
            const nextState: ScoreState = {
                ...prev,
                sets: prev.sets.map(s => ({ ...s })),
                currentGame: { ...prev.currentGame },
                history: pushHistory(prev)
            };

            const opponent = team === 't1' ? 't2' : 't1';

            // --- SUPER TIE-BREAK LOGIC (Set 3) ---
            if (nextState.isSuperTieBreak) {
                nextState.currentGame[team]++;
                const score = nextState.currentGame[team];
                const oppScore = nextState.currentGame[opponent];

                // Win Super Tie-Break (10 points + 2 diff)
                if (score >= 10 && score - oppScore >= 2) {
                    // Win Set & Match
                    nextState.sets[nextState.currentSetIndex][team]++; // Virtually win the "game/set"
                    nextState.isFinished = true;
                    nextState.winner = team;
                    nextState.currentGame = { t1: 0, t2: 0 };
                }
                return nextState;
            }

            // --- TIE-BREAK LOGIC (6-6 in Set) ---
            if (nextState.isTieBreak) {
                nextState.currentGame[team]++;
                const score = nextState.currentGame[team];
                const oppScore = nextState.currentGame[opponent];

                // Win Tie-Break (7 points + 2 diff)
                if (score >= 7 && score - oppScore >= 2) {
                    // Win Set
                    nextState.sets[nextState.currentSetIndex][team]++; // Becomes 7-6
                    nextState.currentGame = { t1: 0, t2: 0 };
                    nextState.isTieBreak = false;

                    // Check if Match Won
                    checkSetWin(nextState, team, config);
                }
                return nextState;
            }

            // --- STANDARD GAME LOGIC ---
            const currentPoints = nextState.currentGame[team];
            const opponentPoints = nextState.currentGame[opponent];

            // 1. Easy Points (0, 15, 30) -> just increments internal index 0->1->2->3
            if (currentPoints < 3) {
                nextState.currentGame[team]++;
                return nextState;
            }

            // 2. At 40 (3)
            if (currentPoints === 3) {
                // If opponent < 40 (0,1,2) -> Win Game
                if (opponentPoints < 3) {
                    winGame(nextState, team, config);
                    return nextState;
                }

                // If opponent === 40 (3) -> Deuce Scenario
                if (opponentPoints === 3) {
                    if (config.goldenPoint) {
                        // Golden Point Active: Next point wins game
                        winGame(nextState, team, config);
                        return nextState;
                    } else {
                        // Advantage Rule: 3-3 -> 4-3 (Adv)
                        nextState.currentGame[team] = 4; // Advantage
                        return nextState;
                    }
                }

                // If opponent === Adv (4) -> Back to Deuce (3-3)
                if (opponentPoints === 4) {
                    nextState.currentGame[opponent] = 3; // Remove Adv from opponent
                    return nextState;
                }
            }

            // 3. At Adv (4) -> Win Game
            if (currentPoints === 4) {
                winGame(nextState, team, config);
                return nextState;
            }

            return nextState;
        });
    }, [config, pushHistory]);

    // Helper to handle Game Win -> Set Update -> Match Check
    const winGame = (state: ScoreState, team: PlayerSide, cfg: MatchConfig) => {
        const opponent = team === 't1' ? 't2' : 't1';

        // Increment Game Count in current Set
        state.sets[state.currentSetIndex][team]++;
        const setScore = state.sets[state.currentSetIndex][team];
        const oppSetScore = state.sets[state.currentSetIndex][opponent];

        // Reset points
        state.currentGame = { t1: 0, t2: 0 };

        // Check Set Win Conditions

        // 1. Standard 6-X win (must be ahead by 2, e.g. 6-4, 6-3...)
        if (setScore === 6 && oppSetScore <= 4) {
            checkSetWin(state, team, cfg);
            return;
        }

        // 2. 7-5 win
        if (setScore === 7 && oppSetScore === 5) {
            checkSetWin(state, team, cfg);
            return;
        }

        // 3. 6-6 -> Trigger Tie-Break in next points
        if (setScore === 6 && oppSetScore === 6) {
            state.isTieBreak = true;
            // Next points will be handled by Tie-Break logic
            return;
        }
    };

    const checkSetWin = (state: ScoreState, team: PlayerSide, cfg: MatchConfig) => {
        // Player won the set
        // Check if Match is won

        // Check if Match Won

        // Note: The current set update is already reflected in state.sets, so we just calculate wins.
        // But wait, winGame updates state.sets IN PLACE.
        // So we need to count strictly.

        let t1Wins = 0;
        let t2Wins = 0;
        state.sets.forEach(s => {
            if (s.t1 > s.t2) t1Wins++;
            if (s.t2 > s.t1) t2Wins++;
        });

        // Determine required sets to win
        let setsToWin = 2;
        if (cfg.format === '1_SET') setsToWin = 1;

        if (team === 't1' && t1Wins === setsToWin) {
            state.isFinished = true;
            state.winner = 't1';
            return;
        }
        if (team === 't2' && t2Wins === setsToWin) {
            state.isFinished = true;
            state.winner = 't2';
            return;
        }

        // If not finished, start new set
        // Special Case: 1 Set All -> Super Tie Break?
        if (cfg.format === '2_SETS_SUPER_TIE_BREAK' && t1Wins === 1 && t2Wins === 1) {
            state.isSuperTieBreak = true;
            state.currentSetIndex++;
            state.sets.push({ t1: 0, t2: 0 }); // New set for entering score (often displayed as points in set 3)
            return;
        }

        // Normal New Set
        state.currentSetIndex++;
        state.sets.push({ t1: 0, t2: 0 });
        state.isTieBreak = false; // Just in case
    };

    return {
        state,
        config,
        setConfig,
        addPoint,
        undo,
        reset
    };
};
